import { Button, Card, CardContent } from '@tark/shared/components';
import { useAuth, useTenantBootstrap } from '@tark/shared/hooks';
import { approveScheduleDraft, discardScheduleDraft, getScheduleDraft, updateScheduleDraft } from '@tark/shared/lib';
import type { TenantScheduleDraft } from '@tark/shared/types';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CoachPageHeader from '../components/CoachPageHeader';
import CoachScheduleDraftEditor from '../components/schedule-drafts/CoachScheduleDraftEditor';
import { buildScheduleDraftUpdate } from '../lib/scheduleDrafts';

export default function CoachScheduleDraftPage() {
  const { draftId: draftIdParam } = useParams<{ draftId: string }>();
  const draftId = Number(draftIdParam);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeTenant, isLoading: tenantLoading } = useTenantBootstrap(Boolean(user));
  const enabled = activeTenant?.config?.coachTimePollsEnabled === true && activeTenant?.config?.coachScheduleDraftsEnabled === true;
  const [draft, setDraft] = useState<TenantScheduleDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !Number.isFinite(draftId) || draftId <= 0) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setDraft(await getScheduleDraft(draftId));
      setDirty(false);
    } catch {
      setError('This draft schedule could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [draftId, enabled]);

  useEffect(() => { void load(); }, [load, activeTenant?.tenant?.id]);

  const persist = async (current: TenantScheduleDraft): Promise<TenantScheduleDraft | null> => {
    try {
      setWorking(true);
      setError(null);
      const saved = await updateScheduleDraft(current.draftId, buildScheduleDraftUpdate(current));
      setDraft(saved);
      setDirty(false);
      return saved;
    } catch {
      setError('Changes could not be saved. Refresh the draft and try again.');
      return null;
    } finally {
      setWorking(false);
    }
  };

  const approve = async () => {
    if (!draft) return;
    const warningText = draft.warnings.length ? ' You are also confirming that you reviewed the warnings.' : '';
    if (!window.confirm(`Publish this schedule? This creates live sessions and student bookings.${warningText}`)) return;
    let current = draft;
    if (dirty) {
      const saved = await persist(current);
      if (!saved) return;
      current = saved;
    }
    try {
      setWorking(true);
      setError(null);
      setDraft(await approveScheduleDraft(current.draftId, current.version, current.warnings.length > 0));
      setDirty(false);
    } catch {
      setError('The schedule could not be published. Review its warnings and try again.');
    } finally {
      setWorking(false);
    }
  };

  const discard = async () => {
    if (!draft || !window.confirm('Discard this draft? The live schedule will not be changed.')) return;
    try {
      setWorking(true);
      setError(null);
      setDraft(await discardScheduleDraft(draft.draftId, draft.version));
      setDirty(false);
    } catch {
      setError('The draft could not be discarded. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  if (tenantLoading || loading) return <div className="flex min-h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  if (!enabled) return <Card variant="default" className="max-w-xl"><CardContent className="space-y-4 py-6"><p className="font-semibold text-text">Draft schedules are not enabled for this workspace.</p><Button variant="outline" onClick={() => navigate('/schedule')}>Back to Schedule</Button></CardContent></Card>;

  return (
    <div className="w-full space-y-6 pb-24 md:pb-8">
      <CoachPageHeader eyebrow="Coach Studio" title="Draft schedule" subtitle="Review family choices, adjust sessions, then publish once." meta={<span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">{activeTenant?.tenant?.displayName}</span>} />
      <Button variant="ghost" leftIcon={<ArrowLeft />} onClick={() => navigate('/schedule/time-polls')}>Time polls</Button>
      {error ? <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600">{error}<button type="button" className="ml-2 font-semibold underline" onClick={() => void load()}>Retry</button></div> : null}
      {draft ? <CoachScheduleDraftEditor draft={draft} dirty={dirty} working={working} onChange={(next) => { setDraft(next); setDirty(true); }} onSave={() => void persist(draft)} onApprove={() => void approve()} onDiscard={() => void discard()} onOpenSchedule={() => navigate('/schedule')} /> : <Card variant="default"><CardContent className="py-6 text-sm text-text-muted">Draft not found.</CardContent></Card>}
    </div>
  );
}
